import React, { useState, useEffect } from 'react';
import { KeyRound, X, Loader2, CheckCircle, Eye, EyeOff, Download, RefreshCw, AlertTriangle, Copy } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { clearMfaGrace } from '../../utils/mfaGrace';

// Two-factor sign-in for the signed-in person: turn it on (authenticator app), keep recovery codes,
// turn it off. Used by Settings › Security (admins) and My account › Security (every web role), so
// an office user the organisation requires 2FA of can actually enrol.

function Row({ label, hint, children }) {
  return (
    <div className="flex items-center justify-between gap-6 border-b border-slate-100 py-3 last:border-0">
      <div className="min-w-0">
        <p className="text-sm font-medium text-slate-800">{label}</p>
        {hint && <p className="mt-0.5 max-w-xl text-xs leading-relaxed text-slate-500">{hint}</p>}
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}

function PasswordInput({ value, onChange, autoFocus }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative max-w-xs">
      <input
        type={show ? 'text' : 'password'}
        value={value}
        onChange={e => onChange(e.target.value)}
        className="input-field pr-9"
        placeholder="Your password"
        autoComplete="current-password"
        autoFocus={autoFocus}
      />
      <button type="button" onClick={() => setShow(s => !s)} aria-label={show ? 'Hide password' : 'Show password'}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
        {show ? <EyeOff size={14} /> : <Eye size={14} />}
      </button>
    </div>
  );
}

// Shown once, straight after enrolment or after making a new set. The server keeps only hashes,
// so this is the person's only chance to keep them.
function RecoveryCodes({ codes, onDone }) {
  const text = codes.join('\n');
  const copy = () => {
    if (!navigator.clipboard?.writeText) { toast.error('Copying isn’t available here — use Download instead.'); return; }
    navigator.clipboard.writeText(text)
      .then(() => toast.success('Recovery codes copied'))
      .catch(() => toast.error('Couldn’t copy — use Download instead.'));
  };
  const download = () => {
    const body = 'Rostery two-factor recovery codes\n'
      + 'Each code works once. Keep them somewhere safe.\n\n' + text + '\n';
    const url = URL.createObjectURL(new Blob([body], { type: 'text/plain' }));
    const a = document.createElement('a');
    a.href = url; a.download = 'rostery-recovery-codes.txt';
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 space-y-4">
      <div className="flex items-start gap-2.5">
        <AlertTriangle size={16} className="mt-0.5 shrink-0 text-amber-600" aria-hidden="true" />
        <div>
          <p className="text-sm font-semibold text-amber-900">Save your recovery codes</p>
          <p className="mt-0.5 text-xs leading-relaxed text-amber-800">
            If you lose your phone, sign in with one of these instead of a code. Each works once.
            This is the only time they are shown.
          </p>
        </div>
      </div>
      <ul className="grid grid-cols-2 gap-x-6 gap-y-1.5 rounded-lg border border-amber-200 bg-white px-4 py-3 font-mono text-sm text-slate-800 max-w-md">
        {codes.map(c => <li key={c} className="select-all">{c}</li>)}
      </ul>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={copy} className="btn-secondary btn-sm gap-1.5">
          <Copy size={12} /> Copy
        </button>
        <button type="button" onClick={download} className="btn-secondary btn-sm gap-1.5">
          <Download size={12} /> Download
        </button>
        <button type="button" onClick={onDone} className="btn-primary btn-sm gap-1.5">
          <CheckCircle size={12} /> I've saved them
        </button>
      </div>
    </div>
  );
}

export default function TwoFactorSettings() {
  const [status, setStatus]           = useState(null); // { mfaEnabled, recoveryCodesRemaining, requiredByOrg, grace }
  const [loading, setLoading]         = useState(true);
  const [step, setStep]               = useState('idle'); // idle | setup | disable | regen
  const [qrCode, setQrCode]           = useState(null);
  const [secret, setSecret]           = useState('');
  const [code, setCode]               = useState('');
  const [password, setPassword]       = useState('');
  const [codes, setCodes]             = useState(null);   // freshly issued recovery codes
  const [working, setWorking]         = useState(false);

  const load = () => api.get('/auth/mfa/status')
    .then(r => setStatus(r.data))
    .catch(() => setStatus(s => s || { mfaEnabled: false }));

  useEffect(() => { load().finally(() => setLoading(false)); }, []);

  const mfaEnabled = !!status?.mfaEnabled;
  const reset = () => { setStep('idle'); setQrCode(null); setSecret(''); setCode(''); setPassword(''); };

  const startSetup = async () => {
    setWorking(true);
    try {
      const { data } = await api.post('/auth/mfa/setup');
      setQrCode(data.qrCode);
      setSecret(data.secret);
      setCode('');
      setStep('setup');
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to start setup'); }
    finally { setWorking(false); }
  };

  const confirmEnable = async () => {
    if (code.length !== 6) return;
    setWorking(true);
    try {
      const { data } = await api.post('/auth/mfa/enable', { code });
      reset();
      setCodes(data.recoveryCodes || null);
      clearMfaGrace();
      await load();
      toast.success('Two-factor authentication enabled');
    } catch (e) { toast.error(e.response?.data?.message || 'Invalid code'); }
    finally { setWorking(false); }
  };

  const confirmDisable = async () => {
    if (!password) return;
    setWorking(true);
    try {
      await api.post('/auth/mfa/disable', { password });
      reset();
      setCodes(null);
      await load();
      toast.success('Two-factor authentication disabled');
    } catch (e) { toast.error(e.response?.data?.message || 'Incorrect password'); }
    finally { setWorking(false); }
  };

  const confirmRegenerate = async () => {
    if (!password) return;
    setWorking(true);
    try {
      const { data } = await api.post('/auth/mfa/recovery-codes', { password });
      reset();
      setCodes(data.recoveryCodes || null);
      await load();
      toast.success('New recovery codes made. The old ones no longer work.');
    } catch (e) { toast.error(e.response?.data?.message || 'Incorrect password'); }
    finally { setWorking(false); }
  };

  if (loading) return <div className="flex justify-center py-8"><div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" /></div>;

  const remaining = status?.recoveryCodesRemaining ?? 0;
  const grace = !mfaEnabled && status?.requiredByOrg ? status.grace : null;

  return (
    <div className="space-y-5">
      {grace && (
        <div className="flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3" role="status">
          <AlertTriangle size={16} className="mt-0.5 shrink-0 text-amber-600" aria-hidden="true" />
          <p className="text-[13px] leading-snug text-amber-900">
            Your organisation requires two-factor authentication.{' '}
            {grace.daysLeft > 0
              ? <>Set it up within <strong>{grace.daysLeft} day{grace.daysLeft === 1 ? '' : 's'}</strong> to keep signing in.</>
              : <>Set it up now to keep signing in.</>}
          </p>
        </div>
      )}

      {/* Current status */}
      <div>
        <Row
          label="Two-Factor Authentication (TOTP)"
          hint="Use an authenticator app (Google Authenticator, Microsoft Authenticator, Authy) to add a second sign-in step"
        >
          <div className="flex items-center gap-3">
            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${mfaEnabled ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
              {mfaEnabled ? 'Enabled' : 'Disabled'}
            </span>
            {!mfaEnabled && step === 'idle' && (
              <button onClick={startSetup} disabled={working} className="btn-primary btn-sm gap-1.5">
                {working ? <Loader2 size={12} className="animate-spin" /> : <KeyRound size={12} />}
                Enable MFA
              </button>
            )}
            {mfaEnabled && step === 'idle' && (
              <button onClick={() => { setCodes(null); setStep('disable'); }} className="btn-secondary btn-sm text-red-600 hover:text-red-700 gap-1.5">
                <X size={12} /> Disable
              </button>
            )}
          </div>
        </Row>
        {mfaEnabled && (
          <Row
            label="Recovery codes"
            hint={`${remaining} of 10 left. Use one to sign in if you don't have your phone. Making a new set cancels the old ones.`}
          >
            {step === 'idle' && (
              <button onClick={() => { setCodes(null); setStep('regen'); }} className="btn-secondary btn-sm gap-1.5">
                <RefreshCw size={12} /> New codes
              </button>
            )}
          </Row>
        )}
      </div>

      {codes && codes.length > 0 && <RecoveryCodes codes={codes} onDone={() => setCodes(null)} />}

      {/* Setup flow */}
      {step === 'setup' && qrCode && (
        <div className="rounded-xl border border-indigo-100 bg-indigo-50 p-5 space-y-4">
          <p className="text-sm font-semibold text-slate-800">Scan this QR code with your authenticator app</p>
          <div className="flex flex-col sm:flex-row items-start gap-6">
            <img src={qrCode} alt="QR code" className="w-40 h-40 rounded-xl border border-slate-200 bg-white p-1 shrink-0" />
            <div className="space-y-3 flex-1">
              <div>
                <p className="text-xs text-slate-500 mb-1">Or enter this secret manually:</p>
                <code className="text-xs font-mono bg-white border border-slate-200 rounded-lg px-3 py-2 block break-all select-all">{secret}</code>
              </div>
              <div>
                <label className="input-label">Enter the 6-digit code to confirm</label>
                <input
                  type="text" inputMode="numeric" maxLength={6}
                  value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="input-field font-mono text-center text-xl tracking-widest"
                  placeholder="000000"
                  autoFocus
                />
              </div>
              <div className="flex gap-2">
                <button onClick={confirmEnable} disabled={working || code.length !== 6} className="btn-primary btn-sm">
                  {working ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle size={12} />}
                  Confirm & Enable
                </button>
                <button onClick={reset} className="btn-secondary btn-sm">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Disable flow */}
      {step === 'disable' && (
        <div className="rounded-xl border border-red-100 bg-red-50 p-5 space-y-3">
          <p className="text-sm font-semibold text-red-800">Disable two-factor authentication</p>
          <p className="text-xs text-red-600">
            Enter your current password to confirm this action. Your recovery codes will stop working.
            {status?.requiredByOrg ? ' Your organisation requires 2FA, so you will need to set it up again.' : ''}
          </p>
          <PasswordInput value={password} onChange={setPassword} autoFocus />
          <div className="flex gap-2">
            <button onClick={confirmDisable} disabled={working || !password} className="btn-sm bg-red-600 text-white hover:bg-red-700 rounded-xl px-4 py-1.5 text-sm font-medium flex items-center gap-1.5">
              {working ? <Loader2 size={12} className="animate-spin" /> : <X size={12} />}
              Disable MFA
            </button>
            <button onClick={reset} className="btn-secondary btn-sm">Cancel</button>
          </div>
        </div>
      )}

      {/* New recovery codes */}
      {step === 'regen' && (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-5 space-y-3">
          <p className="text-sm font-semibold text-slate-800">Make new recovery codes</p>
          <p className="text-xs text-slate-500">Enter your password. Your current codes stop working straight away.</p>
          <PasswordInput value={password} onChange={setPassword} autoFocus />
          <div className="flex gap-2">
            <button onClick={confirmRegenerate} disabled={working || !password} className="btn-primary btn-sm gap-1.5">
              {working ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
              Make new codes
            </button>
            <button onClick={reset} className="btn-secondary btn-sm">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
